import React, { memo, useCallback, useEffect, useMemo } from 'react'
import {
  Table as BaseTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/Table'
import Badge from '../ui/Badge'
import Text from '../ui/Text'
import ResourceCell from './ResourceCell'
import TableFrame from './TableFrame'
import VariableSelect from './VariableSelect'
import { PROGRESSION_TABLE_CLASS, PROGRESSION_TABLE_HEAD_CLASS } from './constants'
import EmptyState from '../ui/EmptyState'
import Loader from '../shared/Loader'
import Pagination, { PER_PAGE_OPTIONS } from '../ui/pagination'
import ProgressionService from '../../services/api/ProgressionService'
import { formatDateTime } from '../../utils/dateHelpers'
import { usePaginatedTable, useVariableOptions } from './hooks'

const EMPTY_MESSAGE = 'Les opérateurs apparaîtront ici une fois les données disponibles.'

const COLUMN_WIDTHS_WITH_VARIABLE = {
  variable: 'tw:w-[15%]',
  user: 'tw:w-[22%]',
  progression: 'tw:w-[18%]',
  resource: 'tw:w-[15%]',
  section: 'tw:w-[15%]',
  date: 'tw:w-[15%]'
}

const COLUMN_WIDTHS_WITHOUT_VARIABLE = {
  user: 'tw:w-[21%]',
  progression: 'tw:w-[18%]',
  resource: 'tw:w-[20%]',
  section: 'tw:w-[19%]',
  date: 'tw:w-[22%]'
}

const SPONTANEOUS_COLUMN_WIDTHS_WITH_VARIABLE = {
  variable: 'tw:w-[18%]',
  user: 'tw:w-[24%]',
  progression: 'tw:w-[18%]',
  resource: 'tw:w-[18%]',
  date: 'tw:w-[22%]'
}

const SPONTANEOUS_COLUMN_WIDTHS_WITHOUT_VARIABLE = {
  user: 'tw:w-[24%]',
  progression: 'tw:w-[30%]',
  resource: 'tw:w-[16%]',
  date: 'tw:w-[30%]'
}

const columnWidths = (spontaneous, withVariable) => {
  if (spontaneous)
    return withVariable ? SPONTANEOUS_COLUMN_WIDTHS_WITH_VARIABLE : SPONTANEOUS_COLUMN_WIDTHS_WITHOUT_VARIABLE
  return withVariable ? COLUMN_WIDTHS_WITH_VARIABLE : COLUMN_WIDTHS_WITHOUT_VARIABLE
}

// The variable column header holds a name of arbitrary length, and TableHeader
// Forbids wrapping: truncate it so it never overflows the neighbouring columns.
const VARIABLE_HEADER_CLASS = 'tw:inline-block tw:max-w-40 tw:align-middle'

const PROGRESSION_VARIANTS = { success: 'success', inProgress: 'progress', danger: 'danger' }
const PROGRESSION_SUCCESS_THRESHOLD = 0.5

const progressionVariant = ratio => {
  if (ratio >= PROGRESSION_SUCCESS_THRESHOLD)
    return PROGRESSION_VARIANTS.success
  if (ratio > 0)
    return PROGRESSION_VARIANTS.inProgress
  return PROGRESSION_VARIANTS.danger
}

const OperatorsProgressionTable = ({
  collectId,
  dateRange,
  draftId,
  operatorId,
  resourceSearch,
  spontaneous,
  variableId,
  variables,
  onSortChange,
  onVariableChange
}) => {
  const { variableOptions, selectedVariable } = useVariableOptions(variables, variableId)
  const widths = columnWidths(spontaneous, Boolean(selectedVariable))
  const columnCount = Object.keys(widths).length

  const fetcher = useCallback(({ page, perPage, sortBy, sortDirection }) => ProgressionService
    .fetchList(collectId, draftId, {
      name: 'operator_progress',
      variableId,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      operatorId,
      resourceSearch,
      page,
      perPage,
      sortBy,
      sortDirection
    }), [collectId, draftId, dateRange.startDate, dateRange.endDate, variableId, operatorId, resourceSearch])

  const filtersKey = useMemo(() => JSON.stringify({
    variableId,
    operatorId,
    resourceSearch,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate
  }), [variableId, operatorId, resourceSearch, dateRange.startDate, dateRange.endDate])

  const {
    rows, isLoading, sortProps, sortBy, sortDirection,
    page, perPage, total, lastPage, setPage, setPerPage
  } = usePaginatedTable({ fetcher, rowsKey: 'rows', filtersKey })

  // Lifted so the export button, which lives outside the card, follows the
  // Sort currently applied on screen.
  useEffect(() => {
    onSortChange?.({ sortBy, sortDirection })
  }, [onSortChange, sortBy, sortDirection])

  const variableSelect = spontaneous ? null : (
    <VariableSelect
      options={variableOptions}
      value={selectedVariable}
      onChange={onVariableChange}
    />
  )

  const isEmpty = rows.length === 0

  const renderVolumeCell = row => {
    if (!draftId)
      return row.answers_count
    const totalResources = row.total_resources || 0
    const ratio = totalResources > 0 ? row.finished_resources / totalResources : 0
    const pct = Math.round(ratio * 100)
    return (
      <>
        <span className="tw:mr-1">{`${row.finished_resources}/${row.total_resources}`}</span>
        <Badge variant={progressionVariant(ratio)}>{`${pct} %`}</Badge>
      </>
    )
  }

  const renderBody = () => {
    if (isEmpty) {
      return (
        <TableRow>
          <TableCell colSpan={columnCount}>
            {isLoading ? <div className="tw:h-52" /> : <EmptyState message={EMPTY_MESSAGE} />}
          </TableCell>
        </TableRow>
      )
    }
    return rows.map(row => (
      <TableRow key={row.user_id}>
        {selectedVariable ? (
          <TableCell>
            <Text lines={2}>{row.last_resource_variable_value || '-'}</Text>
          </TableCell>
        ) : null}
        <TableCell>
          <Text lines={2}>{row.user_name}</Text>
        </TableCell>
        <TableCell>{renderVolumeCell(row)}</TableCell>
        <TableCell>
          <ResourceCell
            collectId={collectId}
            draftId={draftId || row.last_draft_id}
            name={row.last_resource_name}
            resourceId={row.last_resource_id}
            spontaneous={spontaneous}
          />
        </TableCell>
        {spontaneous ? null : (
          <TableCell>
            <Text lines={2}>{row.last_section_name || '-'}</Text>
          </TableCell>
        )}
        <TableCell>{formatDateTime(row.last_updated_at)}</TableCell>
      </TableRow>
    ))
  }

  return (
    <TableFrame framed={!spontaneous} headerRight={variableSelect} title="Opérateurs">
      <div className="tw:relative tw:overflow-x-auto">
        <BaseTable className={PROGRESSION_TABLE_CLASS} hover={!isEmpty} bordered>
          <TableHead className={PROGRESSION_TABLE_HEAD_CLASS}>
            <TableRow>
              {selectedVariable ? (
                <TableHeader
                  className={widths.variable}
                  {...sortProps('last_resource_variable_value')}
                >
                  <Text className={VARIABLE_HEADER_CLASS} truncate>{selectedVariable.label}</Text>
                </TableHeader>
              ) : null}
              <TableHeader className={widths.user} {...sortProps('user_name')}>
                Opérateurs
              </TableHeader>
              <TableHeader
                className={widths.progression}
                {...sortProps(draftId ? 'progression' : 'answers_count')}
              >
                {draftId ? 'Progression' : 'Réponses'}
              </TableHeader>
              <TableHeader className={widths.resource} {...sortProps('last_resource_name')}>
                Dernière ressource
              </TableHeader>
              {spontaneous ? null : (
                <TableHeader className={widths.section} {...sortProps('last_section_name')}>
                  Dernière section
                </TableHeader>
              )}
              <TableHeader className={widths.date} {...sortProps('date')}>
                Date
              </TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>{renderBody()}</TableBody>
        </BaseTable>
        {isLoading ? (
          <div
            className={'tw:absolute tw:inset-x-0 tw:bottom-0 tw:top-12 tw:flex tw:items-center '
              + 'tw:justify-center tw:bg-white/70 tw:z-10'}
          >
            <Loader />
          </div>
        ) : null}
      </div>
      {isEmpty ? null : (
        <Pagination
          disabled={isLoading}
          lastPage={lastPage}
          page={page}
          perPage={perPage}
          perPageOptions={PER_PAGE_OPTIONS}
          total={total}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
        />
      )}
    </TableFrame>
  )
}

export default memo(OperatorsProgressionTable)
